// Resources mutations. Each hook calls the API directly via `getApiClient()`
// and patches/invalidates the QueryClient cache. Server actions are not
// involved.

import {
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import type { operations } from '@leanin/api-client';
import type { ReactionCounts, ReactionKind } from '@leanin/shared/reactions';
import { trackResourceCreated, trackResourceSaved } from '../analytics/events';
import { getApiClient } from '../api-client';
import { unwrap, unwrapVoid } from '../api-error';
import {
  invalidateAndRefetchAll,
  restoreSnapshots,
  type SnapshotSet,
} from '../query-cache-utils';
import { transformAllListCaches } from '../cache/transform-all-list-caches';
import { adminKeys } from '../queries/admin';
import {
  isSavedScopeListKey,
  resourcesKeys,
  type ResourceDetailView,
  type ResourceKind,
  type ResourceListPage,
  type ResourceSummaryView,
} from '../queries/resources';

// After any resource write, refresh the list caches: the public lists plus the
// admin resource namespace (admin list + the "Manage" detail view). The editor
// itself reads an uncached query (`adminKeys.resourceEditor`) and needs no
// invalidation here — that is what keeps a save → reopen cycle showing the
// persisted value (bug #45).
function invalidateResourceLists(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: resourcesKeys.lists() });
  qc.invalidateQueries({ queryKey: adminKeys.resources() });
}

// ---- Inputs ----

export type CreateResourceInput = NonNullable<
  operations['createResource']['requestBody']
>['content']['application/json'];

export type UpdateResourceInput = NonNullable<
  operations['updateResource']['requestBody']
>['content']['application/json'];

// ---- Lifecycle ----

export function useCreateResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateResourceInput) => {
      const client = getApiClient();
      const data = unwrap(
        await client.POST('/api/v1/resources', { body: input }),
      );
      return data.data;
    },
    onSuccess: (data) => {
      trackResourceCreated({ content_type: data.kind });
      invalidateResourceLists(qc);
      qc.invalidateQueries({ queryKey: resourcesKeys.tags().queryKey });
    },
  });
}

export function useUpdateResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      patch: UpdateResourceInput;
    }): Promise<ResourceDetailView> => {
      const client = getApiClient();
      const data = unwrap(
        await client.PATCH('/api/v1/resources/{id}', {
          params: { path: { id: input.id } },
          body: input.patch,
        }),
      );
      return data.data;
    },
    onSuccess: (data, vars) => {
      qc.setQueryData(resourcesKeys.detail(vars.id).queryKey, data);
      invalidateResourceLists(qc);
    },
  });
}

export function useDeleteResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = getApiClient();
      unwrapVoid(
        await client.DELETE('/api/v1/resources/{id}', {
          params: { path: { id } },
        }),
      );
      return id;
    },
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: resourcesKeys.detail(id).queryKey });
      invalidateResourceLists(qc);
    },
  });
}

export function usePublishResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<ResourceDetailView> => {
      const client = getApiClient();
      const data = unwrap(
        await client.POST('/api/v1/resources/{id}/publish', {
          params: { path: { id } },
        }),
      );
      return data.data;
    },
    onSuccess: (data, id) => {
      qc.setQueryData(resourcesKeys.detail(id).queryKey, data);
      invalidateResourceLists(qc);
    },
  });
}

export function useUnpublishResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<ResourceDetailView> => {
      const client = getApiClient();
      const data = unwrap(
        await client.POST('/api/v1/resources/{id}/unpublish', {
          params: { path: { id } },
        }),
      );
      return data.data;
    },
    onSuccess: (data, id) => {
      qc.setQueryData(resourcesKeys.detail(id).queryKey, data);
      invalidateResourceLists(qc);
    },
  });
}

export function useFeatureResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/resources/{id}/feature', {
          params: { path: { id } },
        }),
      );
      return id;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: resourcesKeys.detail(id).queryKey });
      invalidateResourceLists(qc);
    },
  });
}

export function useUnfeatureResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = getApiClient();
      unwrapVoid(
        await client.DELETE('/api/v1/admin/resources/{id}/feature', {
          params: { path: { id } },
        }),
      );
      return id;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: resourcesKeys.detail(id).queryKey });
      invalidateResourceLists(qc);
    },
  });
}

// ---- Circle attachments ----

export function useAttachCircleResource(circleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      readonly resourceId: string;
      readonly note?: string | null;
    }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/circles/{circleId}/resources', {
          params: { path: { circleId } },
          body: {
            resourceId: input.resourceId,
            ...(input.note != null ? { note: input.note } : {}),
          },
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: resourcesKeys.circleAttachments(circleId).queryKey,
      });
    },
  });
}

export function useDetachCircleResource(circleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (resourceId: string) => {
      const client = getApiClient();
      unwrapVoid(
        await client.DELETE(
          '/api/v1/circles/{circleId}/resources/{resourceId}',
          { params: { path: { circleId, resourceId } } },
        ),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: resourcesKeys.circleAttachments(circleId).queryKey,
      });
    },
  });
}

// ---- Save / unsave (optimistic on saved-ids + detail + saved-scope list) ----

type ResourceListInfinite = InfiniteData<ResourceListPage>;

function patchSavedIds(
  qc: ReturnType<typeof useQueryClient>,
  id: string,
  nextSaved: boolean,
): { previous: ReadonlyArray<string> | undefined } {
  const previous = qc.getQueryData<readonly string[]>(
    resourcesKeys.savedIds().queryKey,
  );
  if (previous === undefined) return { previous };
  const set = new Set(previous);
  if (nextSaved) set.add(id);
  else set.delete(id);
  qc.setQueryData<readonly string[]>(resourcesKeys.savedIds().queryKey, [...set]);
  return { previous };
}

function patchDetailSaved(
  qc: ReturnType<typeof useQueryClient>,
  id: string,
  nextSaved: boolean,
): { previousDetail: ResourceDetailView | undefined } {
  const previousDetail = qc.getQueryData<ResourceDetailView>(
    resourcesKeys.detail(id).queryKey,
  );
  if (previousDetail !== undefined) {
    qc.setQueryData<ResourceDetailView>(resourcesKeys.detail(id).queryKey, {
      ...previousDetail,
      viewerSaved: nextSaved,
    });
  }
  return { previousDetail };
}

// Remove a resource from every cached saved-scope list page. Returns
// snapshots so the optimistic edit can be reverted on mutation error.
function removeFromSavedScopeLists(
  qc: QueryClient,
  id: string,
): SnapshotSet {
  return transformAllListCaches<ResourceListInfinite>(
    qc,
    resourcesKeys.lists(),
    (value) => ({
      ...value,
      pages: value.pages.map((page) => ({
        ...page,
        items: page.items.filter((item) => item.id !== id),
      })),
    }),
    isSavedScopeListKey,
  );
}

// Add a resource to the saved-scope list cache by copying its shape from any
// other list page where it appears. If we don't have it cached anywhere, we
// can't optimistically insert (we'd need the full summary shape); the post-
// mutation invalidation will refetch and bring it in.
function insertIntoSavedScopeLists(
  qc: QueryClient,
  id: string,
): SnapshotSet {
  // Find the resource's full shape in any cached list (saved or otherwise).
  const entries = qc.getQueriesData<ResourceListInfinite>({
    queryKey: resourcesKeys.lists(),
  });
  let summary: ResourceSummaryView | undefined;
  for (const [, value] of entries) {
    if (value === undefined) continue;
    for (const page of value.pages) {
      const hit = page.items.find((item) => item.id === id);
      if (hit !== undefined) {
        summary = hit;
        break;
      }
    }
    if (summary !== undefined) break;
  }
  if (summary === undefined) return { previousLists: [] };
  const found = summary;
  return transformAllListCaches<ResourceListInfinite>(
    qc,
    resourcesKeys.lists(),
    (value) => {
      const firstPage = value.pages[0];
      if (firstPage === undefined) return null;
      // Don't double-insert if already present.
      if (firstPage.items.some((item) => item.id === id)) return null;
      const [head, ...rest] = value.pages;
      if (head === undefined) return null;
      return {
        ...value,
        pages: [{ ...head, items: [found, ...head.items] }, ...rest],
      };
    },
    isSavedScopeListKey,
  );
}

export function useSaveResource() {
  const qc = useQueryClient();
  return useMutation({
    // `kind` rides along solely for the analytics payload — the save endpoint
    // only needs the id, but the summary/detail the caller holds always knows
    // the kind, and threading it beats a cache lookup that can miss.
    mutationFn: async ({ id }: { id: string; kind: ResourceKind }) => {
      const client = getApiClient();
      return unwrap(
        await client.POST('/api/v1/resources/{id}/save', {
          params: { path: { id } },
        }),
      );
    },
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: resourcesKeys.savedIds().queryKey });
      await qc.cancelQueries({ queryKey: resourcesKeys.detail(id).queryKey });
      await qc.cancelQueries({ queryKey: resourcesKeys.lists() });
      const savedSnap = patchSavedIds(qc, id, true);
      const detailSnap = patchDetailSaved(qc, id, true);
      const listSnap = insertIntoSavedScopeLists(qc, id);
      return { ...savedSnap, ...detailSnap, ...listSnap };
    },
    onError: (_err, { id }, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(resourcesKeys.savedIds().queryKey, ctx.previous);
      }
      if (ctx?.previousDetail !== undefined) {
        qc.setQueryData(resourcesKeys.detail(id).queryKey, ctx.previousDetail);
      }
      if (ctx?.previousLists !== undefined) {
        restoreSnapshots(qc, { previousLists: ctx.previousLists });
      }
    },
    onSuccess: (_data, { kind }) => {
      trackResourceSaved({ content_type: kind });
    },
    onSettled: (_data, _err, { id }) => {
      qc.invalidateQueries({ queryKey: resourcesKeys.savedIds().queryKey });
      qc.invalidateQueries({ queryKey: resourcesKeys.detail(id).queryKey });
      // Engagement changes the recommendation inputs — a saved resource
      // leaves the candidate pool.
      qc.invalidateQueries({ queryKey: resourcesKeys.recommendedAll() });
      // Saved-scope cache MUST refresh now, not on next tab activation —
      // otherwise the tab switch flashes stale rows over the optimistic
      // patch. See lib/query-cache-utils.ts:invalidateAndRefetchAll.
      invalidateAndRefetchAll(qc, {
        predicate: (q) => isSavedScopeListKey(q.queryKey),
      });
    },
  });
}

export function useUnsaveResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = getApiClient();
      unwrapVoid(
        await client.DELETE('/api/v1/resources/{id}/save', {
          params: { path: { id } },
        }),
      );
      return id;
    },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: resourcesKeys.savedIds().queryKey });
      await qc.cancelQueries({ queryKey: resourcesKeys.detail(id).queryKey });
      await qc.cancelQueries({ queryKey: resourcesKeys.lists() });
      const savedSnap = patchSavedIds(qc, id, false);
      const detailSnap = patchDetailSaved(qc, id, false);
      const listSnap = removeFromSavedScopeLists(qc, id);
      return { ...savedSnap, ...detailSnap, ...listSnap };
    },
    onError: (_err, id, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(resourcesKeys.savedIds().queryKey, ctx.previous);
      }
      if (ctx?.previousDetail !== undefined) {
        qc.setQueryData(resourcesKeys.detail(id).queryKey, ctx.previousDetail);
      }
      if (ctx?.previousLists !== undefined) {
        restoreSnapshots(qc, { previousLists: ctx.previousLists });
      }
    },
    onSettled: (_data, _err, id) => {
      qc.invalidateQueries({ queryKey: resourcesKeys.savedIds().queryKey });
      qc.invalidateQueries({ queryKey: resourcesKeys.detail(id).queryKey });
      // Unsaving returns the resource to the recommendation pool.
      qc.invalidateQueries({ queryKey: resourcesKeys.recommendedAll() });
      invalidateAndRefetchAll(qc, {
        predicate: (q) => isSavedScopeListKey(q.queryKey),
      });
    },
  });
}

// ---- Reactions ----

export function useReactToResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      kind: ReactionKind;
    }): Promise<{
      reactionCounts: ReactionCounts;
      viewerReaction: ReactionKind | null;
      action: 'added' | 'removed';
    }> => {
      const client = getApiClient();
      const data = unwrap(
        await client.POST('/api/v1/resources/{id}/reactions', {
          params: { path: { id: input.id } },
          body: { kind: input.kind },
        }),
      );
      return data.data;
    },
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: resourcesKeys.detail(vars.id).queryKey });
      // Reactions are engagement — they remove the resource from the
      // recommendation pool (or restore it when toggled off).
      qc.invalidateQueries({ queryKey: resourcesKeys.recommendedAll() });
    },
  });
}

// ---- Comments ----
// Create/delete invalidate the detail leaf too: `commentCount` on the
// detail DTO powers the action-bar chip and is recounted server-side.

export function useCreateResourceComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      resourceId: string;
      body: string;
      bodyFormat: 'v1' | 'v2';
    }) => {
      const client = getApiClient();
      const data = unwrap(
        await client.POST('/api/v1/resources/{id}/comments', {
          params: { path: { id: input.resourceId } },
          body: { body: input.body, bodyFormat: input.bodyFormat },
        }),
      );
      return data.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: resourcesKeys.comments(vars.resourceId).queryKey,
      });
      qc.invalidateQueries({
        queryKey: resourcesKeys.detail(vars.resourceId).queryKey,
      });
      // Commenting is engagement — the resource leaves the recommendation pool.
      qc.invalidateQueries({ queryKey: resourcesKeys.recommendedAll() });
    },
  });
}

export function useUpdateResourceComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      commentId: string;
      resourceId: string;
      body: string;
      bodyFormat: 'v1' | 'v2';
    }) => {
      const client = getApiClient();
      const data = unwrap(
        await client.PATCH('/api/v1/resources/comments/{id}', {
          params: { path: { id: input.commentId } },
          body: { body: input.body, bodyFormat: input.bodyFormat },
        }),
      );
      return data.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: resourcesKeys.comments(vars.resourceId).queryKey,
      });
    },
  });
}

export function useDeleteResourceComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { commentId: string; resourceId: string }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.DELETE('/api/v1/resources/comments/{id}', {
          params: { path: { id: input.commentId } },
        }),
      );
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: resourcesKeys.comments(vars.resourceId).queryKey,
      });
      qc.invalidateQueries({
        queryKey: resourcesKeys.detail(vars.resourceId).queryKey,
      });
    },
  });
}

// ---- Media upload (two-step: presign → commit) ----

export function useRequestResourceMediaUpload() {
  return useMutation({
    mutationFn: async (input: {
      id: string;
      contentType: string;
      size: number;
    }) => {
      const client = getApiClient();
      return unwrap(
        await client.POST('/api/v1/resources/{id}/media', {
          params: { path: { id: input.id } },
          body: { contentType: input.contentType, size: input.size },
        }),
      );
    },
  });
}

export function useCommitResourceMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      key: string;
      name?: string;
    }): Promise<ResourceDetailView> => {
      const client = getApiClient();
      const data = unwrap(
        await client.POST('/api/v1/resources/{id}/media/commit', {
          params: { path: { id: input.id } },
          body: {
            key: input.key,
            ...(input.name !== undefined ? { name: input.name } : {}),
          },
        }),
      );
      return data.data;
    },
    onSuccess: (data, vars) => {
      qc.setQueryData(resourcesKeys.detail(vars.id).queryKey, data);
    },
  });
}
